import {
  Box,
  List,
  ListItem,
  Stack,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  useMediaQuery,
  Flex
} from '@chakra-ui/react';
import ListCheckIcon from 'components/atoms/Icon/list-check';
import React from 'react';
import { useCartStore } from 'stores/cart';
import styled from 'styled-components';
import { isMadPawsStore } from '../../../common/util';
/**
 * Renders the content for the medication notice.
 * @returns {JSX.Element}
 */
const renderMedication = ({ color }) => (
  <Stack gap={0}>
    <Text fontWeight={500} fontSize={21} color={color} textAlign="center" mb="32px">
      Prescription Medication
    </Text>
    <Text color={color}>
      You are purchasing an S4 prescription medication that{' '}
      <strong>requires an original written veterinary prescription.</strong> By purchasing this
      product you understand that an original pen signed prescription must be provided prior to
      dispensing by Pet Chemist Pharmacy.
      <br />
      <br />
      <strong>
        We will provide instructions on where to send your original prescription, and collect
        important information relating to your pet after checkout.
      </strong>
    </Text>
  </Stack>
);

/**
 * Renders the content for the cold shipment notice.
 * @returns {JSX.Element}
 */
const renderCold = ({ color }) => (
  <Stack spacing={4}>
    <Text
      fontWeight={500}
      fontSize={21}
      color={isMadPawsStore() ? 'var(--greyscale-dark-4x)' : 'var(--standard-black)'}
      textAlign="center"
      mb="32px"
    >
      Cold Shipping Terms & Conditions
    </Text>
    <Text color={color}>
      This is a prescription medication product that is recommended to be stored at refrigerated
      temperatures. Pet Chemist will ship this product in an esky with ice bricks via Startrack
      Premium, which is not a cold chain delivery service. We do not recommend delivery to regional
      areas or Western Australia.
    </Text>
    <Text color={color}>
      By purchasing this product you agree to our Cold Shipping Terms & Conditions as follows:
    </Text>

    <List m={0}>
      <ListItem display={'flex'} color={color}>
        <Box mr={{ base: '12px', lg: '16px' }} flexShrink={0}>
          <ListCheckIcon {...(!isMadPawsStore() && { color: '#14607a' })} />
        </Box>
        You understand and accept the risk for potential delays to shipping that are beyond the
        control of Pet Chemist.
      </ListItem>
      <ListItem display={'flex'} color={color}>
        <Box mr={{ base: '12px', lg: '16px' }} flexShrink={0}>
          <ListCheckIcon {...(!isMadPawsStore() && { color: '#14607a' })} />
        </Box>
        You understand and accept that Pet Chemist will not replace or refund any refrigerated
        products due to high temperatures, delays in shipping, or any other conditions that may
        affect the viability of this product.
      </ListItem>
      <ListItem display={'flex'} color={color}>
        <Box mr={{ base: '12px', lg: '16px' }} flexShrink={0}>
          <ListCheckIcon {...(!isMadPawsStore() && { color: '#14607a' })} />
        </Box>
        You understand and accept that there are inherent risks involved in shipping refrigerated
        products which may affect the efficacy of this product.
      </ListItem>
    </List>

    <Text color={color}>
      You are purchasing an S4 prescription medication that requires an original written veterinary
      prescription. By purchasing this product you understand that an original pen signed
      prescription must be provided prior to dispensing by Pet Chemist Pharmacy.
    </Text>
    <Text color={color}>
      We will provide instructions on where to send your original prescription, and collect
      important information relating to your pet after checkout.
    </Text>
  </Stack>
);

/**
 * Renders the content for the vet notice.
 * @returns {JSX.Element}
 */
const renderVet = ({ color, product = false }) => (
  <Stack spacing={0}>
    <Text fontWeight={500} fontSize={21} color={color} textAlign="center" mb="32px">
      Customer declaration
    </Text>
    <Text color={color}>
      Veterinary diets are carefully formulated to assist with the treatment or management of a
      particular illness or condition. By purchasing this veterinary diet you acknowledge and agree
      to the below statement.
    </Text>

    <List m={0}>
      <ListItem display={'flex'} gap={0} pt={'24px'} mb="0">
        <Box w="24px" flexShrink={0}>
          <ListCheckIcon {...(!isMadPawsStore() && { color: '#14607a' })} />
        </Box>
        <Text ml={{ base: '12px', lg: '16px' }} color={color}>
          {' '}
          I confirm that <strong>my pet was examined by a veterinarian</strong> who recommended the
          use of this veterinary diet on the basis of their diagnosis;
        </Text>
      </ListItem>
      <ListItem display={'flex'} gap={0} pt={'24px'} mb="0" color={color}>
        <Box w="24px" flexShrink={0}>
          <ListCheckIcon {...(!isMadPawsStore() && { color: '#14607a' })} />
        </Box>
        <Text ml={{ base: '12px', lg: '16px' }}>
          I understand that it is recommended that I seek veterinary advice{' '}
          <strong> at least every 6 months </strong>
          regarding the usage of this veterinary diet; and
        </Text>
      </ListItem>
      <ListItem display={'flex'} gap={0} pt={'24px'} mb="0" color={color}>
        <Box w="24px" flexShrink={0}>
          <ListCheckIcon {...(!isMadPawsStore() && { color: '#14607a' })} />
        </Box>
        <Text ml={{ base: '12px', lg: '16px' }}>
          I understand that I need to <strong>seek immediate veterinary advice</strong> if my pet’s
          condition deteriorates in any way whilst I use this veterinary{' '}
          {product ? 'product' : 'diet'}.
        </Text>
      </ListItem>
    </List>
  </Stack>
);

/**
 * Displays a product notice based on the provided content type.
 *
 * @param {{ content: 'medication' | 'cold' | 'vet' }} props
 * @returns {JSX.Element}
 */
const ProductNotice = ({ content, children }) => {
  const [isPc] = useMediaQuery('(min-width: 687px)', {
    ssr: true,
    fallback: false // return false on the server, and re-evaluate on the client side
  });
  const { prescription, setPrescription } = useCartStore();
  const color = isMadPawsStore() ? 'var(--greyscale-dark-3x)' : 'var(--standard-black)';
  const contentMapping = {
    medication: renderMedication({ color }),
    cold: renderCold({ color }),
    vet: renderVet({ color }),
    vetProduct: renderVet({ color, product: true })
  };
  return (
    <Modal
      isOpen={prescription}
      onClose={() => setPrescription(false)}
      size={isPc ? '' : 'full'}
      isCentered
      scrollBehavior={isPc ? 'outside' : 'inside'}
    >
      <ModalOverlay />
      <ModalContentContainer>
        <ModalContent
          px={{ base: '24px', lg: '72px' }}
          pt={{ base: '40px', lg: '48px' }}
          pb="40px"
          maxW={'687px'}
          borderRadius={{ base: 0, lg: '12px' }}
        >
          <ModalCloseButton
            top={'16px'}
            right={'16px'}
            color={isMadPawsStore() ? '#6B6B6B' : '#202020'}
          />
          <ModalBody p="0">
            <Box>{contentMapping[content]}</Box>
            <Flex mt="32px" justifyContent={'center'}>
              {' '}
              {children}
            </Flex>
          </ModalBody>
        </ModalContent>
      </ModalContentContainer>
    </Modal>
  );
};

const ModalContentContainer = styled.span`
  .chakra-modal__content-container {
    z-index: 2500;
  }
`;

export default ProductNotice;
